"use client";

import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { logger } from "@/utils/logger";

// Schema for profile update
const profileFormSchema = z.object({
  username: z.string().email({
    message: "Username must be a valid email address.",
  }),
  first_name: z.string().min(1, {
    message: "First name is required.",
  }),
  last_name: z.string().min(1, {
    message: "Last name is required.",
  }),
});

// Schema for password change
const passwordFormSchema = z
  .object({
    current_password: z.string().min(1, {
      message: "Current password is required.",
    }),
    new_password: z.string().min(8, {
      message: "New password must be at least 8 characters.",
    }),
    confirm_password: z.string().min(8, {
      message: "Confirm password must be at least 8 characters.",
    }),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords don't match",
    path: ["confirm_password"],
  });

type ProfileFormValues = z.infer<typeof profileFormSchema>;
type PasswordFormValues = z.infer<typeof passwordFormSchema>;

interface AccountProps {
  apiBaseUrl: string;
}

const Account: React.FC<AccountProps> = ({ apiBaseUrl }) => {
  const { user, token, logout, refreshUser } = useAuth();
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("profile"); // State for daisyUI tabs

  // Initialize profile form
  const profileForm = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      username: "",
      first_name: "",
      last_name: "",
    },
  });

  // Initialize password form
  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });

  // Populate profile form with user data when user object is available
  useEffect(() => {
    if (user) {
      profileForm.reset({
        username: user.username || "",
        first_name: user.first_name || "",
        last_name: user.last_name || "",
      });
      // Set initial avatar preview if user has one, ensuring null if undefined
      if (user.avatar_url) {
         const url = getAvatarUrl(user.avatar_url);
         setAvatarPreview(url ?? null);
      }
    }
  }, [user, profileForm]);

  // Handle avatar file selection
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        setAvatarPreview(previewUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  // Get avatar fallback text from user name
  const getAvatarFallback = () => {
    if (!user) return "?";
    const firstName = user.first_name || "";
    const lastName = user.last_name || "";
    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    // Ensure we return something, even if names are empty
    return initials || "??"; 
  };

  // Helper function to ensure absolute URLs for avatars
  const getAvatarUrl = (relativeUrl: string | undefined): string | undefined => {
    if (!relativeUrl) return undefined;

    // If it's already an absolute URL (starts with http:// or https://)
    // This check remains useful if some older data might have absolute URLs
    if (relativeUrl.match(/^https?:\/\//)) {
      logger.debug("Using already absolute avatar URL:", relativeUrl);
      return relativeUrl;
    }

    // Read the API URL from the environment variable (likely passed as apiBaseUrl prop)
    // and derive the base URL for static assets by removing the /api suffix and trailing slash
    const apiUrl = apiBaseUrl; // Prop now represents the full API URL (e.g., https://.../api)
    const staticBaseUrl = apiUrl.replace(/(\/api)?\/?$/, "");

    // Ensure no double slashes if relativeUrl starts with /
    const cleanRelativeUrl = relativeUrl.startsWith('/') ? relativeUrl.substring(1) : relativeUrl;
    // Construct the full URL using the derived static base URL
    const fullUrl = `${staticBaseUrl}/${cleanRelativeUrl}`;
    logger.debug(`Constructed avatar URL: ${fullUrl} from static base: ${staticBaseUrl} and relative: ${relativeUrl}`);
    return fullUrl;
  };

  // Handle profile update submission
  const onProfileSubmit = async (values: ProfileFormValues) => {
    setIsProfileLoading(true);
    setProfileError(null);
    logger.info("Profile update submitted", { username: values.username });
    try {
      let response;
      // Create FormData if avatar is being updated
      if (avatarFile) {
        const formData = new FormData();
        formData.append("username", values.username);
        formData.append("first_name", values.first_name);
        formData.append("last_name", values.last_name);
        formData.append("avatar", avatarFile);

        response = await fetch(`${apiBaseUrl}/users/me`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });
      } else {
        // Use JSON if only updating text fields
        response = await fetch(`${apiBaseUrl}/users/me`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(values),
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update profile");
      }
      
      const updatedUser = await response.json();
      console.log("Profile updated successfully:", updatedUser);

      // Show success message
      alert("Profile updated successfully.");

      // Reset avatar state
      setAvatarFile(null);
      // Keep the preview visible

      // Refresh the user data in the auth context
      await refreshUser();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred";
      setProfileError(errorMessage);
      // Display error message
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsProfileLoading(false);
    }
  };
  // Handle password change submission
  const onPasswordSubmit = async (values: PasswordFormValues) => {
    setIsPasswordLoading(true);
    setPasswordError(null);
    logger.info("Password change submitted");
    try {
      // Create FormData to match the backend's expected format
      const formData = new FormData();
      formData.append("current_password", values.current_password);
      formData.append("new_password", values.new_password);

      const response = await fetch(`${apiBaseUrl}/reset-password`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to change password");
      } // Show success message
      alert("Password changed successfully.");

      // Reset the password form
      passwordForm.reset();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred";
      setPasswordError(errorMessage);
      // Display error message
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsPasswordLoading(false);
    }
  };

  if (!user) {
    return <p className="text-center p-8">Loading user data...</p>;
  }

  // Construct current avatar URL or use preview
  const displayAvatarUrl = avatarPreview;
  const initials = getAvatarFallback();

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6 text-center">Account Settings</h1>

      <div className="max-w-2xl mx-auto">
        {/* daisyUI Tabs */}
        <div role="tablist" className="tabs tabs-lifted tabs-lg grid grid-cols-2 mb-8">
          <a
            role="tab"
            className={`tab ${activeTab === 'profile' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </a>
          <a
            role="tab"
            className={`tab ${activeTab === 'password' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            Change Password
          </a>
        </div>

        {/* Profile Tab Content */}
        <div role="tabpanel" className={`tab-content bg-base-100 border-base-300 rounded-box p-6 ${activeTab !== 'profile' ? 'hidden' : ''}`}>
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <h2 className="card-title">Profile Information</h2>
              <p className="text-sm text-base-content/70 mb-4">Update your account details here.</p>

              {profileError && (
                <div role="alert" className="alert alert-error mb-4">
                   <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2 2m2-2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>{profileError}</span>
                </div>
              )}
              <div className="flex flex-col items-center mb-6">
                <div className={`avatar ${!displayAvatarUrl ? 'placeholder' : ''}`}>
                  <div className="w-24 rounded-full ring ring-primary ring-offset-base-100 ring-offset-2 bg-neutral text-neutral-content">
                    {displayAvatarUrl ? (
                      <img
                        src={displayAvatarUrl}
                        alt={`${user.first_name} ${user.last_name}`}
                      />
                    ) : (
                      <span className="text-3xl">{initials}</span>
                    )}
                  </div>
                </div>

                <div className="mt-4 form-control w-full max-w-xs">
                   <label htmlFor="avatar" className="label">
                     <span className="label-text">Profile Picture</span>
                   </label>
                   <input
                    id="avatar"
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="file-input file-input-bordered file-input-sm w-full max-w-xs"
                  />
                </div>
              </div>

              <form
                onSubmit={profileForm.handleSubmit(onProfileSubmit)}
                className="space-y-4" // Adjusted spacing
              >
                {/* Username/Email Field */}
                <label className="form-control w-full">
                  <div className="label">
                    <span className="label-text">Email</span>
                  </div>
                  <input
                    type="email"
                    placeholder="user@example.com"
                    className={`input input-bordered w-full ${profileForm.formState.errors.username ? 'input-error' : ''}`}
                    disabled={isProfileLoading}
                    {...profileForm.register("username")}
                  />
                  <div className="label">
                     <span className="label-text-alt">This email will be used as your username to log in.</span>
                     {/* Inline error message for username */}
                     {profileForm.formState.errors.username && (
                        <span className="label-text-alt text-error">{profileForm.formState.errors.username.message}</span>
                     )}
                  </div>
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* First Name Field */}
                  <label className="form-control w-full">
                    <div className="label">
                      <span className="label-text">First Name</span>
                    </div>
                    <input
                      type="text"
                      placeholder="John"
                      className={`input input-bordered w-full ${profileForm.formState.errors.first_name ? 'input-error' : ''}`}
                      disabled={isProfileLoading}
                      {...profileForm.register("first_name")}
                    />
                     <div className="label">
                        {/* Inline error message for first_name */}
                        {profileForm.formState.errors.first_name && (
                          <span className="label-text-alt text-error">{profileForm.formState.errors.first_name.message}</span>
                        )}
                    </div>
                  </label>

                  {/* Last Name Field */}
                  <label className="form-control w-full">
                    <div className="label">
                      <span className="label-text">Last Name</span>
                    </div>
                    <input
                      type="text"
                      placeholder="Doe"
                      className={`input input-bordered w-full ${profileForm.formState.errors.last_name ? 'input-error' : ''}`}
                      disabled={isProfileLoading}
                      {...profileForm.register("last_name")}
                    />
                     <div className="label">
                        {/* Inline error message for last_name */}
                       {profileForm.formState.errors.last_name && (
                          <span className="label-text-alt text-error">{profileForm.formState.errors.last_name.message}</span>
                        )}
                    </div>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isProfileLoading}
                  className="btn btn-primary w-full"
                >
                  {isProfileLoading && <span className="loading loading-spinner"></span>}
                  {isProfileLoading ? "Saving..." : "Save Changes"}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Password Tab Content */}
         <div role="tabpanel" className={`tab-content bg-base-100 border-base-300 rounded-box p-6 ${activeTab !== 'password' ? 'hidden' : ''}`}>
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <h2 className="card-title">Change Password</h2>
              <p className="text-sm text-base-content/70 mb-4">
                Update your password here. After saving, you'll be using your new password to log in.
              </p>

              {passwordError && (
                 <div role="alert" className="alert alert-error mb-4">
                   <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2 2m2-2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>{passwordError}</span>
                </div>
              )}

              <form
                onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
                className="space-y-4" // Adjusted spacing
              >
                {/* Current Password Field */}
                <label className="form-control w-full">
                  <div className="label">
                    <span className="label-text">Current Password</span>
                  </div>
                  <input
                    type="password"
                    placeholder="Enter your current password"
                     className={`input input-bordered w-full ${passwordForm.formState.errors.current_password ? 'input-error' : ''}`}
                    disabled={isPasswordLoading}
                    {...passwordForm.register("current_password")}
                  />
                   <div className="label">
                     {/* Inline error message for current_password */}
                     {passwordForm.formState.errors.current_password && (
                       <span className="label-text-alt text-error">{passwordForm.formState.errors.current_password.message}</span>
                     )}
                  </div>
                </label>

                {/* New Password Field */}
                <label className="form-control w-full">
                  <div className="label">
                    <span className="label-text">New Password</span>
                  </div>
                  <input
                    type="password"
                    placeholder="Enter your new password"
                    className={`input input-bordered w-full ${passwordForm.formState.errors.new_password ? 'input-error' : ''}`}
                    disabled={isPasswordLoading}
                    {...passwordForm.register("new_password")}
                  />
                  <div className="label">
                    <span className="label-text-alt">Must be at least 8 characters.</span>
                     {/* Inline error message for new_password */}
                    {passwordForm.formState.errors.new_password && (
                       <span className="label-text-alt text-error">{passwordForm.formState.errors.new_password.message}</span>
                     )}
                  </div>
                </label>

                {/* Confirm New Password Field */}
                <label className="form-control w-full">
                  <div className="label">
                    <span className="label-text">Confirm New Password</span>
                  </div>
                  <input
                    type="password"
                    placeholder="Confirm your new password"
                     className={`input input-bordered w-full ${passwordForm.formState.errors.confirm_password ? 'input-error' : ''}`}
                    disabled={isPasswordLoading}
                    {...passwordForm.register("confirm_password")}
                  />
                   <div className="label">
                      {/* Inline error message for confirm_password */}
                     {passwordForm.formState.errors.confirm_password && (
                       <span className="label-text-alt text-error">{passwordForm.formState.errors.confirm_password.message}</span>
                     )}
                  </div>
                </label>

                <button
                  type="submit"
                  disabled={isPasswordLoading}
                  className="btn btn-primary w-full"
                >
                  {isPasswordLoading && <span className="loading loading-spinner"></span>}
                  {isPasswordLoading
                    ? "Changing Password..."
                    : "Change Password"}
                </button>
              </form>
            </div>
          </div>
        </div>


        <div className="mt-8 text-center">
          <button onClick={logout} className="btn btn-outline">
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default Account;
